import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Layout from '../components/Layout';
import Meta from '../components/Meta';
import Container from '../components/Container';
import Breadcrumb from '../components/Breadcrumb';
import Intro from '../components/Intro';
import Dropzone from '../components/Dropzone';
import Features from '../components/Features';
import Faq from '../components/Faq';
import FileTable from '../components/FileTable';
import ErrorModal from '../components/ErrorModal';
import useUpload from '../utils/useUpload';
import { apiUrl } from '../utils/apiRequest';
import useApiRequest from '../utils/useApiRequest';
import getErrorName from '../utils/getErrorName';
import s, { ConvertResponseStruct } from '../utils/struct';
import { sendEvent } from '../utils/analytics';
import useInterruptRouting from '../utils/useInterruptRouting';
import HowTo from "../components/HowTo";
import { Result } from '../components/Result';

const PageWordToPdf = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'word-to-pdf']);
  const { basePath } = useRouter();

  const [{ step, files, result }, setState] = useState({
    step: 1,
    files: [],
    result: null,
  });
  const [isStepReset, setStepReset] = useInterruptRouting(step);

  const handleGoToStep2 = useCallback((files) => {
    setStepReset(false);
    setState((state) => ({
      ...state,
      step: 2,
      files,
    }));
  }, [setStepReset]);

  const handleGoToStep3 = useCallback((result) => {
    setState((state) => ({
      ...state,
      step: 3,
      result,
    }));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    isStepReset && setState({
      step: 1,
      files: [],
      result: null,
    });
  }, [step, isStepReset]);

  let content;

  if (step === 1) {
    content = (
      <Step1
        onNextStep={handleGoToStep2}
      />
    );
  } else if (step === 2) {
    content = (
      <Step2
        files={files}
        onNextStep={handleGoToStep3}
      />
    );
  } else if (step === 3) {
    content = (
      <Step3
        result={result}
      />
    );
  }

  return (
    <Layout className="page-convert">
      <Meta
        title={t('word-to-pdf:meta.title')}
        description={t('word-to-pdf:meta.description')}
      />
      {content}
      <HowTo title={t('word-to-pdf:howto.title')} steps={t('word-to-pdf:howto.steps', {returnObjects: true})}/>
      <Features
        content={t('word-to-pdf:features.content', { returnObjects: true })}
        style={{ backgroundImage: `url(${basePath}/images/features-3.svg)` }}
      />
      <Faq items={t('word-to-pdf:faq.items', { returnObjects: true })} />
    </Layout>
  );
};

const Step1 = ({ onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'word-to-pdf']);
  const { basePath } = useRouter();
  const [files, setFiles] = useState([]);
  const { upload, uploading, error: uploadError } = useUpload();
  const [showUploadError, setShowUploadError] = useState(false);

  const handleAccepted = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles.map((file) => ({
      file,
    })));
  }, []);

  const handleUploadErrorModalHide = useCallback(() => {
    setShowUploadError(false);
  }, []);

  let errorModal;

  if (uploadError) {
    errorModal = (
      <ErrorModal
        title={t('convert-and-edit:upload-error-modal.title')}
        text={t('convert-and-edit:upload-error-modal.text')}
        show={showUploadError}
        onHide={handleUploadErrorModalHide}
      />
    );
  }

  useEffect(() => {
    const filesToUpload = files.map(({ file }) => file);

    if (!filesToUpload.length) {
      return;
    }

    upload(filesToUpload, (uploadedFiles) => {
      onNextStep(files.map((file, i) => ({
        ...file,
        hash: uploadedFiles[i].hash,
      })));
    });
  }, [files, upload, onNextStep]);

  useEffect(() => {
    if (uploadError) {
      setShowUploadError(true);
    }
  }, [uploadError]);

  return (
    <>
      <Container className="my-xs-3">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            {t('common:nav.index')}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            {t('common:nav.word-to-pdf')}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Container>
      <Intro
        title={t('word-to-pdf:intro.title')}
        text={t('word-to-pdf:intro.text')}
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
      />
      <Container className="my-xs-3 my-sm-4 my-md-5 my-lg-6">
        <Dropzone
          type="word"
          disabled={uploading}
          onAccepted={handleAccepted}
        />
      </Container>
      {errorModal}
    </>
  );
};

const Step2 = ({ files, onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'word-to-pdf']);
  const { basePath } = useRouter();
  const [convertStatus, setConvertStatus] = useState('idle');
  const [showConvertError, setShowConvertError] = useState(false);

  const {
    sendApiRequest,
    processing,
    error: convertError,
  } = useApiRequest({ url: '/api/pdf/from/docx' });

  const handleConvert = useCallback(() => {
    if (processing) {
      return;
    }

    // temporary lock to 1 file
    const file = files[0];

    setConvertStatus('progress');

    sendApiRequest({
      data: {
        name: file.file.name,
        filename: file.hash,
      },
      validateResponse(response) {
        s.assert(response.data, ConvertResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'word-to-pdf',
          action: 'success',
          label: `${time}:${files.length}`,
        });

        let fileType;
        let fileUrl;
        let fileName;
        let fileSize;

        if (response.data.zip) {
          const { url, size } = response.data.zip;

          fileType = 'zip';
          fileUrl = url;
          fileName = 'files-word-to-pdf.zip';
          fileSize = size;
        } else {
          const { url, size } = response.data.result[0];

          fileType = 'pdf';
          fileUrl = url;

          fileName = [
            file.file.name.split('.').slice(0, -1).join('.'),
            url.split('.').pop(),
          ].join('.');

          fileSize = size;
        }

        setConvertStatus('done');

        onNextStep({
          fileType,
          fileName,
          fileUrl: apiUrl(fileUrl),
          fileSize,
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'word-to-pdf',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${files.length}`,
        });

        setConvertStatus('idle');
      },
    });
  }, [files, processing, sendApiRequest, onNextStep]);

  const handleConvertErrorModalHide = useCallback(() => {
    setShowConvertError(false);
  }, []);

  useEffect(() => {
    if (convertError) {
      setShowConvertError(true);
    }
  }, [convertError]);

  return (
    <>
      <Intro
        title={t('word-to-pdf:intro.title')}
        text={t('word-to-pdf:intro.text')}
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
      />
      <Container className="my-xs-3">
        <FileTable
          convertTo="pdf"
          fileType="word"
          fileName={files[0].file.name}
          fileSize={files[0].file.size}
          onConvert={handleConvert}
          convertStatus={convertStatus}
        />
      </Container>
      <ErrorModal
        title={t('convert-and-edit:convert-error-modal.title')}
        text={t('convert-and-edit:convert-error-modal.text')}
        show={showConvertError}
        onHide={handleConvertErrorModalHide}
      />
    </>
  );
};

const Step3 = ({ result }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'word-to-pdf']);
  const actions = {
    edit: true,
    rotate: true,
    unlock: true,
    merge: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }
  return (<Result t={t} result={result} route='/word-to-pdf' actions={actions}/>);
};

Step1.propTypes = {
  onNextStep: PropTypes.func.isRequired,
};

Step2.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({
    file: PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    hash: PropTypes.string,
  })).isRequired,
  onNextStep: PropTypes.func.isRequired,
};

Step3.propTypes = {
  result: PropTypes.shape({
    fileType: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
  }).isRequired,
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'word-to-pdf',
    ]),
  },
});

export default PageWordToPdf;
